import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiDatePicker } from "@/components/MultiDatePicker";
import { StarRating } from "@/components/StarRating";
import { GuestListEditor } from "@/components/GuestListEditor";
import { LinkedGuestsEditor } from "@/components/LinkedGuestsEditor";
import { GuestNameAutocomplete } from "@/components/GuestNameAutocomplete";
import { DayShiftEditor } from "@/components/DayShiftEditor";
import { ViewingsSelect } from "@/components/ViewingsSelect";
import { TimeSelect } from "@/components/TimeSelect";
import { FIELDS, PROFILE_FIELDS, computeDerived, formatValue, hoursForDays, plusOneHour, minusOneHour, shiftEndFromStart, deriveStatus, statusColor, ALL_STATUSES, autoTourTitle, withNewTourDefaults, DEFAULT_SHIFT, syncDayShifts, subsequentDayShift, sumShiftHours, sumComp, dayTourHours, rollupShifts, toViewingsArray, PROMO_OPTIONS, PROMO_DISCOUNT, applyPromoDiscount, gratuityColor, gratuityPctColor, getSettings, normalizeTourType, sameGuestName, groupGuestsByFamily } from "@/lib/fields";
import { ImageZoomViewer } from "@/components/ImageZoomViewer";
import { useFieldConfig } from "@/contexts/FieldConfigContext";
import { Save, X, Sparkles, User, Users, Calculator, Tag, Camera, UploadCloud, Loader2, Clock, Gift, Check, ChevronDown, ChevronRight, MessageSquare, CalendarDays, Star, SlidersHorizontal, AlertTriangle } from "lucide-react";
import { fileToResizedDataUrl } from "@/lib/image";
import { api } from "@/lib/api";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Fields we never diff on a re-upload: complex/relational values or figures that are ROLLED UP
// from the per-day shifts (so overwriting the top-level mirror wouldn't stick anyway).
const SHEET_SKIP_KEYS = new Set([
  "tour_date", "day_shifts", "guest_list", "guest_photos", "party_photo", "viewings",
  "poc_profile", "tour_sheet_image", "days", "tip_percent", "avg_per_guest",
  "retail_value", "total_hours", "comp_time",
  // Party size is derived from the named guest list (or the guide's manual edit), never
  // reconciled from the sheet's Adults/Children header — a mismatch surfaces as the
  // in-form "which number?" prompt instead of the re-upload conflict dialog.
  "party_size", "adults", "children",
]);
const sheetLabelFor = (k) =>
  (FIELDS.find((f) => f.key === k) || {}).label ||
  (PROFILE_FIELDS.find((f) => f.key === k) || {}).label || k;
const sameSheetVal = (a, b) => {
  const na = Number(a), nb = Number(b);
  if (a !== "" && b !== "" && a != null && b != null && !isNaN(na) && !isNaN(nb)) return na === nb;
  return String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
};
const showSheetVal = (v) => (v === true ? "Yes" : v === false ? "No" : v === "" || v == null ? "—" : String(v));

// Normalize incoming fields: party size from guest list, days/hours from dates, per-day shift times.
const buildInit = (fields) => {
  const init = { ...(fields || {}) };
  if (Array.isArray(init.guest_list)) init.guest_list = groupGuestsByFamily(init.guest_list);
  const dates = Array.isArray(init.tour_date) ? init.tour_date : (init.tour_date ? [init.tour_date] : []);
  if (dates.length > 0) {
    init.days = dates.length;
    // Anchor day-1 to the sheet's meeting time (the guest PICKUP) when there's no explicit
    // guide shift: shift = pickup−1h … pickup+tourLen+1h. Only fall back to the generic
    // 8–5 default when the sheet gives no time at all.
    // Day-1 fallback shift must use PER-DAY hours, not the tour-wide total. total_hours is the
    // sum across all days (e.g. 14 for a 2-day, 7-hr/day tour), so divide by the day count —
    // otherwise a 2-day tour would push day-1's shift end 16h out (past midnight).
    const tourLen = (init.total_hours !== undefined && init.total_hours !== null && init.total_hours !== "" && Number(init.total_hours) > 0)
      ? Number(init.total_hours) / dates.length : (getSettings().tourHours || 7);
    let fb;
    if (init.shift_start || init.shift_end) {
      fb = { start: init.shift_start || DEFAULT_SHIFT.start, end: init.shift_end || DEFAULT_SHIFT.end };
    } else if (init.meeting_time) {
      const st = minusOneHour(init.meeting_time);
      fb = { start: st, end: shiftEndFromStart(st, tourLen), meet_time: init.meeting_time };
    } else {
      fb = DEFAULT_SHIFT;
    }
    let ds = syncDayShifts(dates, init.day_shifts, fb, subsequentDayShift());
    // Ensure each day carries its own meeting location + pickup time + comp. Default the
    // location to the tour's existing single location (day-1 location for multiday tours),
    // and seed day-1 comp from any existing top-level comp_time so it isn't lost.
    const existingComp = Number(init.comp_time);
    const baseRate = (init.cost_per_hour !== undefined && init.cost_per_hour !== null && init.cost_per_hour !== "" && Number(init.cost_per_hour) > 0) ? Number(init.cost_per_hour) : "";
    ds = ds.map((d, i) => ({
      ...d,
      meet_location: (d.meet_location !== undefined && d.meet_location !== null && d.meet_location !== "") ? d.meet_location : (init.resort_meet || ""),
      meet_time: d.meet_time || (i === 0 && init.meeting_time ? init.meeting_time : (d.start ? plusOneHour(d.start) : "")),
      comp: (d.comp !== undefined && d.comp !== null && d.comp !== "") ? Number(d.comp) : (i === 0 && !isNaN(existingComp) ? existingComp : 0),
      hours: dayTourHours(d),
      rate: (d.rate !== undefined && d.rate !== null && d.rate !== "") ? Number(d.rate) : baseRate,
    }));
    init.day_shifts = ds;
    // Top-level mirrors are derived from the per-day shifts (day-1 values + summed totals + avg rate).
    Object.assign(init, rollupShifts(ds, init.cost_per_hour));
  }
  if (init.meeting_time && (init.shift_start === undefined || init.shift_start === null || init.shift_start === "")) {
    init.shift_start = minusOneHour(init.meeting_time);
  }
  init.viewings = toViewingsArray(init.viewings);
  return init;
};

export const PreviewDialog = ({ open, onOpenChange, initialFields, initialProfile, returnMatch, onSave, title, inline = false, saveLabel, onDirtyChange, saveApiRef, onTourNameState, onExtractingChange, onRequestLeave, newTour = false }) => {
  const [values, setValues] = useState({});
  const navigate = useNavigate();
  const { fields: effFields, customFields } = useFieldConfig();
  const openProfile = (n) => {
    const name = String(n || "").trim();
    if (!name) return;
    const path = `/profile/${encodeURIComponent(name)}`;
    if (onRequestLeave) onRequestLeave(path);
    else navigate(path);
  };
  const [profile, setProfile] = useState({});
  // Field keys the scanner flagged as low-confidence (amber "Verify" highlight). Cleared as
  // the guide edits each flagged field, and reset when opening a saved tour.
  const [uncertainFields, setUncertainFields] = useState(new Set());
  const [snapshot, setSnapshot] = useState("");
  const photoRef = useRef(null);
  const sheetRef = useRef(null);
  const tipTypeRef = useRef(null);
  const [returnState, setReturnState] = useState(null);
  const [returnPromptOpen, setReturnPromptOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  // Re-upload reconciliation: fields the fresh sheet read DIFFERENTLY from what's saved.
  const [pendingSheet, setPendingSheet] = useState(null);
  const [sheetPromptOpen, setSheetPromptOpen] = useState(false);
  // Per-field selection for the reconcile dialog: { "<scope>-<key>": true } = apply the sheet's value.
  const [sheetSelected, setSheetSelected] = useState({});
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tourSheetCollapsed") || "{}"); } catch { return {}; }
  });
  const toggleSection = (secId) => setCollapsed((c) => {
    const next = { ...c, [secId]: !c[secId] };
    try { localStorage.setItem("tourSheetCollapsed", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  const [pocProfile, setPocProfile] = useState({});
  const [viewer, setViewer] = useState({ open: false, src: null, alt: "" });
  const setPocField = (key, val) => setPocProfile((p) => ({ ...p, [key]: val }));

  const galleryPhotos = () => {
    const g = values.guest_photos;
    if (Array.isArray(g) && g.length) return g;
    return values.party_photo ? [values.party_photo] : [];
  };
  const setGallery = (arr) => {
    setField("guest_photos", arr);
    setField("party_photo", arr[0] || null);
  };
  const addGuestPhotos = async (files) => {
    if (!files || !files.length) return;
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        if (!/(png|jpe?g|webp|heic|heif)/i.test(f.type)) continue;
        urls.push(await fileToResizedDataUrl(f));
      }
      if (urls.length) setGallery([...galleryPhotos(), ...urls]);
    } catch {
      toast.error("Could not read that image");
    }
  };
  const removeGuestPhoto = (i) => setGallery(galleryPhotos().filter((_, idx) => idx !== i));

  const handleUploadSheet = async (file) => {
    if (!file) return;
    if (!/(png|jpe?g|webp)/i.test(file.type)) { toast.error("Upload a PNG, JPG or WEBP Tour Sheet"); return; }
    setExtracting(true);
    try {
      const dataUrl = await fileToBase64(file);
      const { fields, profile: prof, return_match, uncertain } = await api.extract(dataUrl);
      const isExisting = !!values.id;

      if (isExisting) {
        // RE-UPLOAD onto a saved tour. Two-part behaviour:
        //  1) Non-destructive: silently fill any BLANK field from the fresh read.
        //  2) Conflicts (sheet read a DIFFERENT value than what's saved) are collected and the
        //     guide is asked "X changed — Update all / Keep mine" (covers guest-identity fields too).
        // The tour image is always swapped in.
        const incoming = { ...fields };
        if (incoming.tour_date && !Array.isArray(incoming.tour_date)) incoming.tour_date = [incoming.tour_date];
        if (incoming.tour_type) incoming.tour_type = normalizeTourType(incoming.tour_type); // "Premium VIP" -> "Premium"
        delete incoming.poc_profile;
        const incomingProfile = prof || {};
        const hasVal = (v) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);

        // 1) fill blanks (scalar tour fields + profile), always swap image
        const merged = { ...values };
        Object.keys(incoming).forEach((k) => {
          if (k === "tour_sheet_image" || k === "guest_list" || SHEET_SKIP_KEYS.has(k)) return;
          if (hasVal(incoming[k]) && !hasVal(merged[k])) merged[k] = incoming[k];
        });
        // Guest list: fill it when the tour has none yet, or refresh it when the sheet lists
        // MORE people than what's saved (the sheet is the source of truth for who's on the
        // tour — e.g. a converted future request starts empty, or an earlier read missed a
        // group). Never shrink a list the guide may have curated.
        const inNames = (incoming.guest_list || []).filter((g) => g && g.name && String(g.name).trim());
        const curNames = (merged.guest_list || []).filter((g) => g && g.name && String(g.name).trim());
        if (inNames.length > 0 && inNames.length > curNames.length) {
          merged.guest_list = incoming.guest_list;
        }
        merged.tour_sheet_image = dataUrl;
        // Party size defaults to the number of named guests; keep the sheet's header count
        // (Adults/Children) to cross-check and prompt the guide when the two disagree.
        const namedEx = (merged.guest_list || []).filter((g) => g && g.name && String(g.name).trim()).length;
        const headerEx = Number(fields.party_size) || ((Number(fields.adults) || 0) + (Number(fields.children) || 0)) || 0;
        if (namedEx > 0) merged.party_size = namedEx;
        if (namedEx > 0 && headerEx > 0 && headerEx !== namedEx) merged._sheet_party_size = headerEx;
        setValues(buildInit(merged));
        setUncertainFields(new Set(uncertain || []));
        const mergedProfile = { ...profile };
        PROFILE_FIELDS.forEach((f) => {
          if (hasVal(incomingProfile[f.key]) && !hasVal(mergedProfile[f.key])) mergedProfile[f.key] = incomingProfile[f.key];
        });
        setProfile(mergedProfile);

        // 2) detect conflicts against the values the guide already saved
        const conflicts = [];
        Object.keys(incoming).forEach((k) => {
          if (k === "tour_sheet_image" || SHEET_SKIP_KEYS.has(k)) return;
          // Kathy ~ Katherine etc. is the SAME guest — don't flag a name change.
          if (k === "guest_name" && sameGuestName(incoming[k], values[k])) return;
          if (hasVal(incoming[k]) && hasVal(values[k]) && !sameSheetVal(incoming[k], values[k])) {
            conflicts.push({ scope: "tour", key: k, label: sheetLabelFor(k), oldVal: values[k], newVal: incoming[k] });
          }
        });
        PROFILE_FIELDS.forEach((f) => {
          const k = f.key;
          if (hasVal(incomingProfile[k]) && hasVal(profile[k]) && !sameSheetVal(incomingProfile[k], profile[k])) {
            conflicts.push({ scope: "profile", key: k, label: f.label, oldVal: profile[k], newVal: incomingProfile[k] });
          }
        });

        if (conflicts.length) {
          const sel = {};
          conflicts.forEach((c) => { sel[`${c.scope}-${c.key}`] = true; });
          setSheetSelected(sel);
          setPendingSheet({ conflicts });
          setSheetPromptOpen(true);
          toast.success(`Tour Sheet re-read — filled blanks. ${conflicts.length} field${conflicts.length === 1 ? "" : "s"} changed — review below.`);
        } else {
          toast.success("Tour Sheet re-read — blank fields filled; everything else already matches.");
        }
        return;
      }

      // NEW tour (first upload): pre-fill everything from the sheet.
      const base = applyPromoDiscount(withNewTourDefaults({ ...fields }));
      if (base.tour_date && !Array.isArray(base.tour_date)) base.tour_date = [base.tour_date];
      base.tour_sheet_image = dataUrl;
      // Party size defaults to the number of named guests; keep the sheet header count
      // (Adults/Children) to cross-check and prompt the guide when the two disagree.
      const namedNew = (base.guest_list || []).filter((g) => g && g.name && String(g.name).trim()).length;
      const headerNew = Number(fields.party_size) || ((Number(fields.adults) || 0) + (Number(fields.children) || 0)) || 0;
      if (namedNew > 0) base.party_size = namedNew;
      if (namedNew > 0 && headerNew > 0 && headerNew !== namedNew) base._sheet_party_size = headerNew;
      if (values.party_photo) base.party_photo = values.party_photo;
      const pp = base.poc_profile || {};
      delete base.poc_profile;
      setPocProfile(pp);
      setValues(buildInit(base));
      setProfile(prof || {});
      setReturnState(return_match || null);
      setUncertainFields(new Set(uncertain || []));
      const promoMsg = PROMO_OPTIONS.includes(base.promo) ? ` — ${base.promo}: 20% off applied` : "";
      const giftMsg = base.gifted_to ? ` · POC ${base.gifted_to} → their own profile` : "";
      toast.success((return_match?.exact ? "Tour Sheet read — returning guest detected" : "Tour Sheet read — review the details below") + promoMsg + giftMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that Tour Sheet");
    } finally {
      setExtracting(false);
    }
  };

  // Apply the sheet's values over the saved ones. `onlySelected` (default) applies only the
  // fields the guide left checked in the reconcile dialog; the rest keep their saved values.
  // Shift-derived fields (meet location/time, start/end, rate) must be written into the
  // per-day shift so the top-level mirror doesn't get re-derived back to the old value.
  const applySheetChanges = (onlySelected = true) => {
    if (!pendingSheet) return;
    const all = pendingSheet.conflicts || [];
    const conflicts = onlySelected ? all.filter((c) => sheetSelected[`${c.scope}-${c.key}`]) : all;
    if (conflicts.length === 0) {
      setSheetPromptOpen(false);
      setPendingSheet(null);
      toast.success("Kept your saved values");
      return;
    }
    const tourKeys = conflicts.filter((c) => c.scope === "tour");
    const profKeys = conflicts.filter((c) => c.scope === "profile");
    if (tourKeys.length) {
      setValues((v) => {
        const next = { ...v };
        const ds = Array.isArray(next.day_shifts) ? next.day_shifts.map((d) => ({ ...d })) : [];
        tourKeys.forEach(({ key, newVal }) => {
          next[key] = newVal;
          if (ds[0]) {
            if (key === "resort_meet") ds[0].meet_location = newVal;
            if (key === "meeting_time") ds[0].meet_time = newVal;
            if (key === "shift_start") ds[0].start = newVal;
            if (key === "shift_end") ds[0].end = newVal;
          }
          if (key === "cost_per_hour") ds.forEach((d) => { d.rate = Number(newVal); });
        });
        if (ds.length) next.day_shifts = ds;
        return buildInit(next);
      });
    }
    if (profKeys.length) {
      setProfile((p) => {
        const np = { ...p };
        profKeys.forEach(({ key, newVal }) => { np[key] = newVal; });
        return np;
      });
    }
    setSheetPromptOpen(false);
    setPendingSheet(null);
    toast.success(`Updated ${conflicts.length} field${conflicts.length === 1 ? "" : "s"} from the sheet`);
  };
  const keepMineOnSheet = () => {
    setSheetPromptOpen(false);
    setPendingSheet(null);
    toast.success("Kept your saved values");
  };
  const toggleSheetField = (c) =>
    setSheetSelected((s) => ({ ...s, [`${c.scope}-${c.key}`]: !s[`${c.scope}-${c.key}`] }));



  useEffect(() => {
    if (open) {
      const init = { ...(initialFields || {}) };
      const pp = init.poc_profile || {};
      delete init.poc_profile;
      const v = buildInit(init);
      setValues(v);
      setProfile(initialProfile || {});
      setPocProfile(pp);
      setReturnState(returnMatch || null);
      setUncertainFields(new Set());
      setSnapshot(JSON.stringify({ values: v, profile: initialProfile || {}, pocProfile: pp }));
    }
  }, [open, initialFields, initialProfile, returnMatch]);

  // Touching a flagged field counts as verifying it — drop its amber highlight.
  const clearFlag = (key) => setUncertainFields((s) => {
    if (!s.has(key)) return s;
    const n = new Set(s); n.delete(key); return n;
  });

  const setField = (key, val) => {
    clearFlag(key);
    setValues((v) => {
      const next = { ...v, [key]: val };
      if (key === "days") {
        next.total_hours = hoursForDays(val);
        next.shift_end = shiftEndFromStart(next.shift_start, next.total_hours);
      }
      if (key === "total_hours") next.shift_end = shiftEndFromStart(next.shift_start, val);
      if (key === "shift_start") {
        next.meeting_time = plusOneHour(val);
        next.shift_end = shiftEndFromStart(val, next.total_hours);
      }
      if (key === "meeting_time") {
        next.shift_start = minusOneHour(val);
        next.shift_end = shiftEndFromStart(next.shift_start, next.total_hours);
      }
      return next;
    });
  };

  // Changing the promo no longer alters any rate — the per-day rate stays the full,
  // non-discounted rate. The 20% promo is applied only to Total Retail (computeDerived).
  const changePromo = (newPromo) =>
    setValues((v) => ({ ...v, promo: newPromo || "" }));

  // Selected tour dates drive per-day shifts -> top-level mirrors (day-1 loc/time + totals).
  const setTourDates = (arr) =>
    setValues((v) => {
      let day_shifts = syncDayShifts(arr, v.day_shifts, DEFAULT_SHIFT, subsequentDayShift());
      // New days inherit the day-1 meeting location and a default rate for multiday tours.
      const dayOneLoc = (day_shifts[0] && day_shifts[0].meet_location) || v.resort_meet || "";
      const baseRate = (v.cost_per_hour !== undefined && v.cost_per_hour !== null && v.cost_per_hour !== "" && Number(v.cost_per_hour) > 0) ? Number(v.cost_per_hour) : "";
      day_shifts = day_shifts.map((d) => ({ ...d, meet_location: (d.meet_location !== undefined && d.meet_location !== null && d.meet_location !== "") ? d.meet_location : dayOneLoc, rate: (d.rate !== undefined && d.rate !== null && d.rate !== "") ? Number(d.rate) : baseRate }));
      const next = { ...v, tour_date: arr, day_shifts };
      if (arr && arr.length > 0) {
        next.days = arr.length;
        Object.assign(next, rollupShifts(day_shifts, v.cost_per_hour));
      }
      return next;
    });

  // Editing per-day shifts recomputes the top-level mirrors (day-1 loc/time + summed totals + avg rate).
  const setDayShifts = (list) =>
    setValues((v) => {
      const next = { ...v, day_shifts: list };
      if (list.length > 0) Object.assign(next, rollupShifts(list, v.cost_per_hour));
      return next;
    });

  // Guest list drives party size (count of named guests) unless the guide has manually set it.
  const setGuestList = (list) =>
    setValues((v) => {
      const named = (list || []).filter((g) => g.name && String(g.name).trim()).length;
      const next = { ...v, guest_list: list };
      if (named > 0 && !v.party_size_manual) next.party_size = named;
      return next;
    });

  // Manual party-size override (editable field + discrepancy prompt). Marks the size as
  // guide-set so editing the guest list afterwards won't silently overwrite it.
  const setPartySize = (val) => {
    setUncertainFields((s) => { const n = new Set(s); n.delete("party_size"); n.delete("adults"); n.delete("children"); return n; });
    setValues((v) => ({ ...v, party_size: val === "" ? "" : Number(val), party_size_manual: true }));
  };
  const resolvePartySize = (val) => {
    setUncertainFields((s) => { const n = new Set(s); n.delete("party_size"); n.delete("adults"); n.delete("children"); return n; });
    setValues((v) => { const n = { ...v, party_size: Number(val), party_size_manual: true }; delete n._sheet_party_size; return n; });
  };

  const setProfileField = (key, val) => { clearFlag(key); setProfile((p) => ({ ...p, [key]: val })); };

  // When an existing guest is picked, auto-load their saved profile (city, phone, email…).
  const loadExistingProfile = async (name) => {
    if (!name) return;
    try {
      const prof = await api.getProfile(name);
      setProfile((p) => {
        const next = { ...p };
        PROFILE_FIELDS.forEach((f) => {
          if ((next[f.key] === undefined || next[f.key] === null || next[f.key] === "") && prof[f.key]) {
            next[f.key] = prof[f.key];
          }
        });
        return next;
      });
      toast.success(`Loaded ${name}'s saved details`);
    } catch {
      // New guest with no profile yet — nothing to load.
    }
  };

  // A likely (non-exact) return match prompts the guide to confirm it's the same person.
  useEffect(() => {
    if (returnState && !returnState.exact && returnState.name) setReturnPromptOpen(true);
  }, [returnState]);

  const confirmReturnGuest = () => {
    if (returnState?.name) setField("guest_name", returnState.name);
    setField("return_trip", true);
    setReturnState((s) => (s ? { ...s, exact: true } : s));
    setReturnPromptOpen(false);
    toast.success(`Marked as a return — linked to ${returnState?.name || "existing guest"}`);
  };
  const declineReturnGuest = () => { setReturnState(null); setReturnPromptOpen(false); };

  // "Mix" tips must be fully split into Cash + Electronic (sum === gratuity) before saving.
  // Old tours that were already "Mix" with no split are grandfathered in (left as-is).
  const wasLegacyMix = (initialFields?.tip_type === "Mix")
    && (initialFields?.tip_cash == null || initialFields?.tip_cash === "")
    && (initialFields?.tip_electronic == null || initialFields?.tip_electronic === "");
  const mixSplitError = () => {
    if (values.tip_type !== "Mix") return null;
    const grat = Number(values.gratuity) || 0;
    if (grat <= 0) return null;
    const bothEmpty = (values.tip_cash == null || values.tip_cash === "")
      && (values.tip_electronic == null || values.tip_electronic === "");
    if (wasLegacyMix && bothEmpty) return null;
    const sum = (Number(values.tip_cash) || 0) + (Number(values.tip_electronic) || 0);
    if (Math.abs(sum - grat) > 0.005) {
      return `Cash + Electronic must add up to the Mix total of $${grat.toLocaleString()} (currently $${sum.toLocaleString()}).`;
    }
    return null;
  };
  const mixError = mixSplitError();

  const handleSave = () => {
    if (mixError) { toast.error(mixError); return Promise.reject(new Error(mixError)); }
    const cleaned = { ...values };
    delete cleaned._sheet_party_size;
    delete cleaned.party_size_manual;
    effFields.forEach((f) => {
      if (f.type === "number" && !f.computed && cleaned[f.key] !== undefined && cleaned[f.key] !== null && cleaned[f.key] !== "") {
        const n = Number(cleaned[f.key]);
        cleaned[f.key] = isNaN(n) ? null : n;
      }
    });
    const payload = { ...cleaned, ...computeDerived(cleaned) };
    if (cleaned.gifted_to && Object.values(pocProfile).some((v) => v)) payload.poc_profile = pocProfile;
    return onSave(payload, profile);
  };

  // Per-day "Save & mark complete" (multi-day tours): persists the current time/hour edits
  // AND flips that day's logged flag in one save, so post-tour reminders stop for that day only.
  const handleToggleDayComplete = async (date) => {
    if (mixError) { toast.error(mixError); return; }
    const cur = values.days_logged || [];
    const isDone = cur.map(String).includes(String(date));
    const nextLogged = isDone ? cur.filter((d) => String(d) !== String(date)) : [...cur, date];
    const cleaned = { ...values, days_logged: nextLogged };
    delete cleaned._sheet_party_size;
    delete cleaned.party_size_manual;
    effFields.forEach((f) => {
      if (f.type === "number" && !f.computed && cleaned[f.key] !== undefined && cleaned[f.key] !== null && cleaned[f.key] !== "") {
        const n = Number(cleaned[f.key]);
        cleaned[f.key] = isNaN(n) ? null : n;
      }
    });
    const payload = { ...cleaned, ...computeDerived(cleaned) };
    if (cleaned.gifted_to && Object.values(pocProfile).some((v) => v)) payload.poc_profile = pocProfile;
    await onSave(payload, profile);
  };

  const derived = computeDerived(values);
  const dirty = snapshot && JSON.stringify({ values, profile, pocProfile }) !== snapshot;

  const handleDiscard = () => {
    try {
      const s = JSON.parse(snapshot);
      setValues(s.values || {});
      setProfile(s.profile || {});
      setPocProfile(s.pocProfile || {});
      toast.info("Changes discarded");
    } catch { /* noop */ }
  };

  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { if (onExtractingChange) onExtractingChange(extracting); }, [extracting, onExtractingChange]);
  if (saveApiRef) saveApiRef.current = { save: handleSave, discard: handleDiscard, dirty, setField, uploadSheet: () => sheetRef.current?.click() };

  // Report the tour name + auto placeholder up so the page header can be the single
  // place to view/edit the tour name (the on-form Tour Name box was removed).
  useEffect(() => {
    if (!onTourNameState) return;
    const auto = autoTourTitle(values);
    const dates = Array.isArray(values.tour_date) ? values.tour_date : (values.tour_date ? [values.tour_date] : []);
    const hasSeed = !!(values.guest_name && String(values.guest_name).trim()) || dates.length > 0;
    onTourNameState({ name: values.tour_name || "", placeholder: hasSeed ? auto : "Name this tour" });
  }, [values.tour_name, values.guest_name, values.tour_date, values.gifted_to, onTourNameState]);

  // Renders a single labeled field control by key, so the layout below can group
  // related fields into explicit rows/sections instead of a flat DOM-order flow.
  const renderField = (key, opts = {}) => {
    const f = effFields.find((x) => x.key === key);
    if (!f) return null;
    // Avg Gratuity/Day only makes sense for multi-day tours — hide on single-day.
    if (key === "avg_per_guest") {
      const dc = Array.isArray(values.tour_date) ? values.tour_date.length : (values.tour_date ? 1 : 0);
      if (dc <= 1) return null;
    }
    const fullWidth = opts.fullWidth || f.type === "textarea";
    const flagged = uncertainFields.has(f.key);
    return (
      <div key={f.key} className={`${fullWidth ? "md:col-span-2" : ""} ${flagged ? "-m-1 rounded-lg bg-amber-400/[0.04] p-1 ring-1 ring-amber-400/50" : ""}`} data-testid={flagged ? `verify-field-${f.key}` : undefined}>
        <div className="mb-1.5 flex items-center gap-1.5">
          <Label className="block text-xs uppercase tracking-wide text-zinc-400">{opts.label || (f.key === "cost_per_hour" ? "Average Hourly Rate / Total Retail" : f.label)}</Label>
          {flagged && (
            <span title="The scanner wasn't fully sure it read this correctly — double-check it against the sheet." className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-700 uppercase tracking-wide text-amber-200" data-testid={`verify-badge-${f.key}`}>
              <AlertTriangle className="h-3 w-3" /> Verify
            </span>
          )}
        </div>
        {f.type === "bool" ? (
          <div className="flex h-10 items-center gap-3 rounded-md border border-zinc-800 bg-zinc-900 px-3">
            <Switch
              checked={f.key === "host_again" ? values[f.key] !== false : !!values[f.key]}
              onCheckedChange={(c) => setField(f.key, c)}
              data-testid={`field-${f.key}`}
            />
            <span className="text-sm text-zinc-300">{(f.key === "host_again" ? values[f.key] !== false : values[f.key]) ? "Yes" : "No"}</span>
          </div>
        ) : f.computed ? (
          <div className="flex h-10 items-center justify-between gap-2 rounded-md border border-dashed border-zinc-700 bg-zinc-900/50 px-3" data-testid={`field-${f.key}`}>
            <span className="text-sm font-500 text-zinc-200">{formatValue(f, derived[f.key])}</span>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-zinc-400">
              <Calculator className="h-3 w-3" /> auto
            </span>
          </div>
        ) : f.type === "status" ? (
          <Select value={values.status_override || "auto"} onValueChange={(v) => setField("status_override", v === "auto" ? null : v)}>
            <SelectTrigger className={`h-10 border-0 font-600 focus:ring-2 focus:ring-[#E0575B]/70 ${statusColor(deriveStatus(values))}`} data-testid={`field-${f.key}`}>
              <span className="flex min-w-0 items-center gap-1">
                <span className="truncate">{deriveStatus(values)}</span>
                {!values.status_override && <span className="shrink-0 text-[10px] font-400 opacity-70">· auto</span>}
              </span>
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-950">
              <SelectItem value="auto" data-testid="field-booking_status-auto">Auto (by tour date)</SelectItem>
              {ALL_STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`field-booking_status-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : f.type === "rating" ? (
          <StarRating
            value={values[f.key]}
            onChange={(n) => setField(f.key, n)}
            testid={`field-${f.key}`}
          />
        ) : f.type === "select" ? (
          <Select value={values[f.key] ?? ""} onValueChange={(v) => setField(f.key, v)}>
            <SelectTrigger ref={f.key === "tip_type" ? tipTypeRef : undefined} className="border-zinc-800 bg-zinc-900 focus:ring-[#E0575B]/70" data-testid={`field-${f.key}`}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-950">
              {f.options.map((opt) => (
                <SelectItem key={opt} value={opt} data-testid={`field-${f.key}-${opt}`}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : f.type === "dates" ? (
          <MultiDatePicker
            value={values[f.key]}
            onChange={setTourDates}
            testid={`field-${f.key}`}
          />
        ) : f.type === "textarea" ? (
          <Textarea
            value={values[f.key] ?? ""}
            onChange={(e) => setField(f.key, e.target.value)}
            className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
            rows={3}
            data-testid={`field-${f.key}`}
          />
        ) : f.type === "date" ? (
          <Input
            type="date"
            value={(() => { const m = String(values[f.key] || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : ""; })()}
            onChange={(e) => { const m = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})$/); setField(f.key, m ? `${+m[2]}/${+m[3]}/${m[1]}` : ""); }}
            className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
            data-testid={`field-${f.key}`}
          />
        ) : (["meeting_time", "shift_start", "shift_end"].includes(f.key)) ? (
          <TimeSelect
            value={values[f.key]}
            onChange={(v) => setField(f.key, v)}
            testid={`field-${f.key}`}
            className="w-full border-zinc-800 bg-zinc-900 focus:ring-[#E0575B]/70"
          />
        ) : f.key === "gratuity" ? (
          <div className="flex items-center gap-2">
            <Input type="number" value={values.gratuity ?? ""} onChange={(e) => setField("gratuity", e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); tipTypeRef.current?.click(); } }} className={`flex-1 border-zinc-800 bg-zinc-900 font-600 focus-visible:ring-[#E0575B]/70 ${gratuityColor(derived.tip_percent)}`} data-testid="field-gratuity" />
            <span className={`shrink-0 whitespace-nowrap text-sm font-600 ${gratuityPctColor(derived.tip_percent)}`} data-testid="field-tip-percent-inline">{derived.tip_percent !== null && derived.tip_percent !== undefined && derived.tip_percent !== "" ? `(${derived.tip_percent}%)` : "(—)"}</span>
          </div>
        ) : f.key === "viewings" ? (
          <ViewingsSelect
            value={values.viewings}
            onChange={(v) => setField("viewings", v)}
            testid={`field-${f.key}`}
          />
        ) : f.key === "cost_per_hour" ? (
          <div className="flex items-center gap-2" data-testid="field-cost_per_hour">
            <div className="flex h-10 flex-1 items-center rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm font-500 text-zinc-100" data-testid="field-avg-rate">
              {values.cost_per_hour !== null && values.cost_per_hour !== undefined && values.cost_per_hour !== "" && Number(values.cost_per_hour) > 0 ? `$${Number(values.cost_per_hour).toLocaleString(undefined, { maximumFractionDigits: 2 })}/hr` : "—"}
            </div>
            {!opts.rateOnly && (
              <span className="shrink-0 whitespace-nowrap text-sm text-zinc-300" data-testid="field-retail-inline">{derived.retail_value !== null && derived.retail_value !== undefined && derived.retail_value !== "" ? `/ $${Number(derived.retail_value).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "/ —"}</span>
            )}
          </div>
        ) : f.key === "promo" ? (
          <div className="space-y-1.5">
            <Select value={values.promo || "none"} onValueChange={(v) => changePromo(v === "none" ? "" : v)}>
              <SelectTrigger className="border-zinc-800 bg-zinc-900 focus:ring-[#E0575B]/70" data-testid="field-promo">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-950">
                <SelectItem value="none" data-testid="field-promo-none">None</SelectItem>
                {PROMO_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt} data-testid={`field-promo-${opt}`}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {PROMO_OPTIONS.includes(values.promo) && (() => {
              const discounted = Number(derived.retail_value) || 0;
              const full = discounted > 0 ? Math.round(discounted / (1 - PROMO_DISCOUNT)) : 0;
              const savings = full - discounted;
              return (
                <div className="space-y-0.5" data-testid="promo-discount-note">
                  <span className="inline-flex items-center gap-1 text-[11px] font-500 text-[#E0575B]">
                    <Tag className="h-3 w-3" /> {Math.round(PROMO_DISCOUNT * 100)}% off · full rate shown
                  </span>
                  {savings > 0 && (
                    <div className="text-[11px] text-zinc-500" data-testid="promo-savings">
                      Guest saved ${savings.toLocaleString()} off ${full.toLocaleString()} full retail
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : (
          <Input
            type={f.type === "number" ? "number" : "text"}
            step={f.step}
            value={values[f.key] ?? ""}
            onChange={(e) => setField(f.key, e.target.value)}
            className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
            data-testid={`field-${f.key}`}
          />
        )}
      </div>
    );
  };

  // Tappable section divider header — collapses/expands its section.
  const SectionHead = ({ id, label, icon: Icon }) => (
    <button
      type="button"
      onClick={() => toggleSection(id)}
      className="group mb-3 flex w-full items-center gap-2.5 text-left"
      data-testid={`section-toggle-${id}`}
      aria-expanded={!collapsed[id]}
    >
      {Icon && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#9B2226]/15 text-[#E0575B] transition-colors group-hover:bg-[#9B2226]/25">
          <Icon className="h-3.5 w-3.5" />
        </span>
      )}
      <span className="font-display text-sm font-700 tracking-tight text-zinc-200">{label}</span>
      <div className="h-px flex-1 bg-gradient-to-r from-zinc-800 to-transparent" />
      <ChevronDown className={`h-4 w-4 shrink-0 text-zinc-600 transition-transform duration-200 group-hover:text-zinc-400 ${collapsed[id] ? "-rotate-90" : ""}`} />
    </button>
  );

  // Guest-list summary + collapse state (collapsed by default, open only when explicitly opened).
  // Prefer AGE-based counts (age < 18 = child; 18+ or no age = adult) whenever any ages are
  // listed, so the header verifies the split against the real guest ages; otherwise fall back to
  // the stored adults/children, then party_size.
  const glRows = Array.isArray(values.guest_list)
    ? values.guest_list.filter((g) => g && String(g.name || "").trim())
    : [];
  const hasAge = (g) => g.age !== "" && g.age !== null && g.age !== undefined && !isNaN(Number(g.age));
  const glWithAges = glRows.filter(hasAge);
  let glAdults;
  let glChildren;
  let glTotal;
  if (glRows.length > 0) {
    // The named guest list is the source of truth for how many people are on the tour
    // (a multi-group Club 33 sheet can list far more names than the header's Adults/Children).
    glTotal = glRows.length;
    if (glWithAges.length > 0) {
      glChildren = glWithAges.filter((g) => Number(g.age) < 18).length;
      glAdults = glTotal - glChildren; // rows without an age count as adults
    } else {
      // No ages listed. Use the header's adults/children split only if it matches the list size;
      // otherwise the header is stale, so treat everyone as adults.
      const ha = Number(values.adults) || 0;
      const hc = Number(values.children) || 0;
      if ((ha || hc) && ha + hc === glTotal) {
        glAdults = ha; glChildren = hc;
      } else {
        glAdults = glTotal; glChildren = 0;
      }
    }
  } else {
    glAdults = Number(values.adults) || 0;
    glChildren = Number(values.children) || 0;
    glTotal = (glAdults + glChildren) || Number(values.party_size) || 0;
  }
  const guestListSummary = glTotal > 0
    ? ((glAdults || glChildren)
        ? `${glTotal} total · ${glAdults} adult${glAdults === 1 ? "" : "s"}, ${glChildren} ${glChildren === 1 ? "child" : "children"}`
        : `${glTotal} guest${glTotal === 1 ? "" : "s"}`)
    : "Tap to add guests";
  // Party size = the number the guide reports. Defaults to the named guest count; the sheet's
  // header count (Adults/Children) is kept only to flag a mismatch and let the guide choose.
  const guestCount = glRows.length;
  const sheetPartySize = Number(values._sheet_party_size) || 0;
  const partyDiscrepancy = sheetPartySize > 0 && guestCount > 0 && sheetPartySize !== guestCount;
  // The scanner reports uncertainty on adults/children/party_size as a group; surface it on the party-size row.
  const partySizeFlagged = uncertainFields.has("party_size") || uncertainFields.has("adults") || uncertainFields.has("children");
  const guestListOpen = collapsed["guest-list"] === false;
  const toggleGuestList = () => setCollapsed((c) => {
    const next = { ...c, "guest-list": c["guest-list"] === false };
    try { localStorage.setItem("tourSheetCollapsed", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const body = (
    <>
        {!inline && (
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl tracking-tight">
            <Sparkles className="h-4 w-4 text-[#E0575B]" />
            {title || "Review guest details"}
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-400">Check the details, edit anything, then save to your database.</DialogDescription>
        </DialogHeader>
        )}

        <button
          type="button"
          onClick={() => !extracting && sheetRef.current?.click()}
          className={`group relative flex w-full items-center gap-3.5 overflow-hidden rounded-2xl border px-4 py-3.5 text-left transition-colors duration-200 ${extracting ? "border-[#9B2226]/50 bg-[#9B2226]/15" : "border-white/10 bg-gradient-to-br from-[#9B2226]/15 to-[#9B2226]/5 hover:border-[#E0575B]/50 hover:from-[#9B2226]/25 hover:to-[#9B2226]/10"} hidden`}
          data-testid="dialog-upload-sheet"
        >
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${extracting ? "bg-[#9B2226]/25 text-[#E0575B]" : "bg-[#9B2226]/25 text-[#E0575B] group-hover:bg-[#9B2226]/40"}`}>
            {extracting ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-display text-sm font-700 tracking-tight text-zinc-100">
              {extracting ? "Reading your Tour Sheet…" : "Upload a Tour Sheet"}
            </span>
            <span className="block text-xs text-zinc-400">
              {extracting ? "Pulling out the details now" : "Snap or share a screenshot — we'll fill in the details for you"}
            </span>
          </span>
          {!extracting && <ChevronRight className="h-5 w-5 shrink-0 text-zinc-500 transition-colors duration-200 group-hover:text-[#E0575B]" />}
        </button>
        <input
          ref={sheetRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          data-testid="dialog-upload-input"
          onChange={(e) => handleUploadSheet(e.target.files?.[0])}
        />

        {/* Booking status — full width, kept above the guest name */}
        <div data-testid="booking-status-row">
          {renderField("booking_status")}
        </div>

        {/* Guest area — full width so long names are fully visible */}
        <div className="rounded-2xl border border-[#9B2226]/30 bg-[#9B2226]/10 px-4 py-4" data-testid="guests-section">
          <label className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#E0575B]/80">
            <User className="h-3.5 w-3.5" /> Lead Guest <span className="normal-case tracking-normal text-zinc-400">— titles the tour; add more to link profiles</span>
          </label>
          <GuestNameAutocomplete
            value={values.guest_name ?? ""}
            onChange={(v) => setField("guest_name", v)}
            onPick={loadExistingProfile}
            onOpen={openProfile}
            testid="field-guest_name"
          />
          <div className="mt-2">
            <LinkedGuestsEditor value={values.linked_guests} onChange={(list) => setField("linked_guests", list)} exclude={values.guest_name} onOpen={openProfile} testid="dialog-linked" />
          </div>
          {/* Return toggle now lives with the guest fields */}
          <div className="mt-3 border-t border-[#9B2226]/20 pt-3">
            {renderField("return_trip")}
          </div>
        </div>

        {/* Guest list — right below the lead guest area, collapsed by default */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40" data-testid="section-guest-list">
          <button
            type="button"
            onClick={toggleGuestList}
            className="group flex w-full items-center gap-2.5 px-4 py-3 text-left"
            data-testid="guest-list-toggle"
            aria-expanded={guestListOpen}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#9B2226]/15 text-[#E0575B] transition-colors group-hover:bg-[#9B2226]/25"><Users className="h-3.5 w-3.5" /></span>
            <span className="font-display text-sm font-700 tracking-tight text-zinc-200">Guest List</span>
            <span className="truncate text-xs text-zinc-400" data-testid="guest-list-summary">{guestListSummary}</span>
            <div className="flex-1" />
            <ChevronDown className={`h-4 w-4 shrink-0 text-zinc-600 transition-transform duration-200 group-hover:text-zinc-400 ${guestListOpen ? "" : "-rotate-90"}`} />
          </button>
          {guestListOpen && (
            <div className="border-t border-zinc-800 px-4 py-4">
              <GuestListEditor value={values.guest_list} onChange={setGuestList} testid="guest-list-editor" hideHeader />
            </div>
          )}
          {/* Party size — editable; defaults to the named guest count. */}
          <div className="flex flex-wrap items-center gap-3 border-t border-zinc-800 px-4 py-3" data-testid="party-size-row">
            <label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-400" htmlFor="party-size-input">
              <Users className="h-3.5 w-3.5 text-[#E0575B]" /> Party size
            </label>
            <Input
              id="party-size-input"
              type="number"
              min="0"
              inputMode="numeric"
              value={values.party_size ?? ""}
              onChange={(e) => setPartySize(e.target.value)}
              className={`h-9 w-24 bg-zinc-900 focus-visible:ring-[#E0575B]/70 ${partySizeFlagged ? "border-amber-400/60 ring-1 ring-amber-400/50" : "border-zinc-800"}`}
              data-testid="field-party_size"
            />
            {partySizeFlagged && (
              <span title="The scanner wasn't fully sure about the party size — double-check it against the sheet." className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-700 uppercase tracking-wide text-amber-200" data-testid="verify-badge-party_size">
                <AlertTriangle className="h-3 w-3" /> Verify
              </span>
            )}
            <span className="text-xs text-zinc-500">{guestCount > 0 ? `${guestCount} name${guestCount === 1 ? "" : "s"} listed` : "edit anytime"}</span>
          </div>
          {partyDiscrepancy && (
            <div className="border-t border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="party-size-discrepancy">
              <p className="text-sm font-600 text-amber-300">
                Guest list has <b className="font-800">{guestCount}</b> name{guestCount === 1 ? "" : "s"}, but the sheet header shows <b className="font-800">{sheetPartySize}</b>. Which is the party size?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => resolvePartySize(guestCount)}
                  className="inline-flex items-center gap-1 rounded-md bg-[#9B2226] px-3 py-1.5 text-xs font-700 text-white hover:bg-[#7A1A1E]"
                  data-testid="party-size-use-guests"
                >
                  Use guest count ({guestCount})
                </button>
                <button
                  type="button"
                  onClick={() => resolvePartySize(sheetPartySize)}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-400/50 px-3 py-1.5 text-xs font-700 text-amber-300 hover:bg-amber-500/15"
                  data-testid="party-size-use-header"
                >
                  Use header ({sheetPartySize})
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-zinc-500">Or type your own number above.</p>
            </div>
          )}
        </div>

        {returnState && returnState.name && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-indigo-400/50 bg-indigo-500/15 px-4 py-3" data-testid="return-match-banner">
            <span className="text-sm font-600 text-indigo-300">
              {returnState.exact
                ? <>Returning guest — matched <b className="font-800">{returnState.name}</b>. Marked as Return.</>
                : <>Possible returning guest: <b className="font-800">{returnState.name}</b>. Same person?</>}
            </span>
            {!returnState.exact && (
              <button onClick={() => setReturnPromptOpen(true)} className="shrink-0 rounded-md border border-indigo-400/60 px-2.5 py-1 text-xs font-700 text-indigo-300 hover:bg-indigo-500/25" data-testid="confirm-return-btn">
                Review
              </button>
            )}
          </div>
        )}

        <div className="space-y-6 py-2" data-testid="tour-fields">
          {/* Schedule: tour date + per-day shifts (location, times, pickup, comp) with live totals */}
          <div data-testid="section-schedule">
            <SectionHead id="schedule" label="Schedule" icon={CalendarDays} />
            {!collapsed.schedule && (
            <div className="space-y-4">
              <div>
                {renderField("tour_date")}
              </div>
              <div className="rounded-2xl border border-zinc-800/40 bg-zinc-500/[0.04] p-4" data-testid="day-shifts-section">
                <div className="mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#E0575B]" />
                  <h3 className="font-display text-sm font-700 tracking-tight text-zinc-200">Shift &amp; Tour Hours</h3>
                </div>
                <DayShiftEditor dayShifts={values.day_shifts || []} onChange={setDayShifts} testid="dialog-day-shifts" loggedDates={values.days_logged || []} onToggleDay={(!newTour && values.id) ? handleToggleDayComplete : undefined} />
                <div className="mt-3 flex flex-wrap items-center justify-end gap-x-4 gap-y-1 border-t border-zinc-800/30 pt-2 text-xs">
                  <span className="text-zinc-400">Total tour hours: <b className="text-zinc-300" data-testid="schedule-total-hours">{sumShiftHours(values.day_shifts || [])} hrs</b></span>
                  <span className="text-zinc-400">Total comp: <b className="text-zinc-300" data-testid="schedule-total-comp">{sumComp(values.day_shifts || [])} hrs</b></span>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Tour details: type + cost type side-by-side; then rate/promo */}
          <div data-testid="section-tour-details">
            <SectionHead id="tour-details" label="Tour Details" icon={Tag} />
            {!collapsed["tour-details"] && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {renderField("tour_type")}
                {renderField("cost_type")}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {renderField("cost_per_hour", { rateOnly: true, label: "Avg Hourly Rate" })}
                {renderField("retail_value", { label: "Total Retail" })}
                {renderField("promo", { label: "Promo" })}
              </div>
              {renderField("co_guide")}
            </div>
            )}
          </div>

          {/* Post-tour metrics */}
          <div data-testid="section-post-tour">
            <SectionHead id="post-tour" label="Post-Tour" icon={Star} />
            {!collapsed["post-tour"] && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {renderField("notes")}
              {renderField("rating")}
              <div className="grid grid-cols-2 gap-3 md:col-span-2">
                {renderField("gratuity")}
                {renderField("tip_type")}
              </div>
              {values.tip_type === "Mix" && (
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 md:col-span-2" data-testid="tip-mix-split">
                  <div>
                    <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Cash</Label>
                    <Input type="number" step="0.01" inputMode="decimal" value={values.tip_cash ?? ""} onChange={(e) => setField("tip_cash", e.target.value)} placeholder="0" className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70" data-testid="field-tip-cash" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Electronic</Label>
                    <Input type="number" step="0.01" inputMode="decimal" value={values.tip_electronic ?? ""} onChange={(e) => setField("tip_electronic", e.target.value)} placeholder="0" className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70" data-testid="field-tip-electronic" />
                  </div>
                  {(() => {
                    const grat = Number(values.gratuity) || 0;
                    const sum = (Number(values.tip_cash) || 0) + (Number(values.tip_electronic) || 0);
                    const diff = grat - sum;
                    if (grat <= 0) return <p className="col-span-2 text-xs text-zinc-500" data-testid="tip-mix-hint">Enter the Mix gratuity above, then split it into Cash + Electronic.</p>;
                    if (Math.abs(diff) <= 0.005) return <p className="col-span-2 inline-flex items-center gap-1 text-xs font-600 text-emerald-400" data-testid="tip-mix-hint"><Check className="h-3.5 w-3.5" /> Adds up to the ${grat.toLocaleString()} Mix total — tax totals use this split.</p>;
                    return <p className="col-span-2 text-xs font-600 text-amber-400" data-testid="tip-mix-hint">Cash + Electronic must equal ${grat.toLocaleString()} — {diff > 0 ? `$${diff.toLocaleString()} left to assign` : `$${Math.abs(diff).toLocaleString()} over`}.</p>;
                  })()}
                </div>
              )}
              <div className="md:col-span-2" data-testid="gratuity-claim-block">
                <label className="inline-flex cursor-pointer select-none items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!values.gratuity_claimed}
                    onChange={(e) => setField("gratuity_claimed", e.target.checked)}
                    className="h-4 w-4 accent-[#9B2226]"
                    data-testid="field-gratuity-claimed"
                  />
                  <span className="text-sm text-zinc-300">Claimed a gratuity?</span>
                </label>
                {values.gratuity_claimed && (
                  <div className="mt-2">
                    <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">How much did you claim?</Label>
                    <Input
                      type="number"
                      value={values.gratuity_claimed_amount ?? ""}
                      onChange={(e) => setField("gratuity_claimed_amount", e.target.value)}
                      placeholder="0"
                      className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
                      data-testid="field-gratuity-claimed-amount"
                    />
                  </div>
                )}
              </div>
              {renderField("avg_per_guest")}
              {renderField("guest_food")}
              {renderField("viewings", { fullWidth: true })}
              {renderField("host_again")}
            </div>
            )}
          </div>

          {customFields.length > 0 && (
            <div data-testid="section-custom-fields">
              <SectionHead id="custom-fields" label="Custom Fields" icon={SlidersHorizontal} />
              {!collapsed["custom-fields"] && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {customFields.map((cf) => renderField(cf.key, { fullWidth: cf.type === "textarea" }))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-1 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid="party-photo-section">
          <div className="mb-3 flex items-center gap-2">
            <Camera className="h-4 w-4 text-[#E0575B]" />
            <h3 className="font-display text-sm font-700 tracking-tight text-zinc-200">Photos</h3>
            <span className="text-xs text-zinc-400">— tap to view full screen &amp; zoom</span>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="min-w-0 flex-1">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Guest Photos</Label>
              <div className="flex flex-wrap gap-2">
                {galleryPhotos().map((src, i) => (
                  <div key={i} className="relative">
                    <img
                      src={src}
                      alt={`Guest ${i + 1}`}
                      draggable={false}
                      onClick={() => setViewer({ open: true, src, alt: "Guest Photo" })}
                      className="h-24 w-24 cursor-zoom-in select-none rounded-lg border border-zinc-800 object-cover transition-opacity hover:opacity-90"
                      data-testid={`guest-photo-${i}`}
                    />
                    <button type="button" onClick={() => removeGuestPhoto(i)} className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow transition-colors hover:bg-red-500" data-testid={`guest-photo-remove-${i}`} aria-label="Remove photo">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => photoRef.current?.click()} className="flex h-24 w-24 items-center justify-center rounded-lg border border-dashed border-zinc-700 text-center text-xs text-zinc-400 transition-colors hover:border-[#9B2226]/50 hover:text-zinc-300" data-testid="guest-photo-add">
                  <Camera className="h-5 w-5" />
                </button>
              </div>
              <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="party-photo-input" onChange={(e) => { addGuestPhotos(e.target.files); if (photoRef.current) photoRef.current.value = ""; }} />
            </div>

            {values.tour_sheet_image && (
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Tour Sheet</Label>
                <div className="flex items-start gap-3">
                  <img
                    src={values.tour_sheet_image}
                    alt="Tour Sheet"
                    onClick={() => setViewer({ open: true, src: values.tour_sheet_image, alt: "Tour Sheet" })}
                    className="h-28 w-28 cursor-zoom-in rounded-lg border border-zinc-800 object-cover transition-opacity hover:opacity-90"
                    data-testid="preview-tour-sheet-image"
                  />
                  <div className="flex flex-col gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => sheetRef.current?.click()} className="border-zinc-700 bg-transparent" data-testid="tour-sheet-replace">Replace</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setField("tour_sheet_image", null)} className="text-red-400 hover:text-red-300" data-testid="tour-sheet-remove">Remove</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {values.gifted_to && (
          <div className="mt-1 rounded-lg border border-[#9B2226]/40 bg-[#9B2226]/10 p-4" data-testid="poc-profile-card">
            <div className="mb-1 flex items-center gap-2">
              <Gift className="h-4 w-4 text-[#E0575B]" />
              <h3 className="font-display text-sm font-700 tracking-tight text-zinc-100">Gifted to {values.gifted_to}</h3>
            </div>
            <p className="mb-3 text-xs text-[#E0575B]" data-testid="poc-profile-note">
              These details are pulled from the notes and will be saved to <b>{values.gifted_to}</b>'s guest page (not the main guest's).
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {PROFILE_FIELDS.map((f) => {
                const val = pocProfile[f.key] ?? "";
                const tel = String(val).replace(/[^\d+]/g, "");
                return (
                <div key={f.key}>
                  <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">{f.label}</Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={val}
                      onChange={(e) => setPocField(f.key, e.target.value)}
                      className="flex-1 border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
                      data-testid={`poc-profile-${f.key}`}
                    />
                    {f.key === "phone" && val && (
                      <a href={`sms:${tel}`} title="Text POC" data-testid="poc-phone-text" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#9B2226]/50 bg-[#9B2226]/10 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20">
                        <MessageSquare className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-1 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
          <div className="mb-3 flex items-center gap-2">
            <User className="h-4 w-4 text-[#E0575B]" />
            <h3 className="font-display text-sm font-700 tracking-tight text-zinc-200">{values.gifted_to ? `Main guest profile — ${values.guest_name || ""}` : "Guest Profile"}</h3>
            <span className="text-xs text-zinc-400">— pulled from the sheet, shared across this guest's tours</span>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2" data-testid="profile-location-row">
              {PROFILE_FIELDS.filter((f) => ["city", "state", "country"].includes(f.key)).map((f) => (
                <div key={f.key}>
                  <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">{f.label}</Label>
                  <Input
                    value={profile[f.key] ?? ""}
                    onChange={(e) => setProfileField(f.key, e.target.value)}
                    className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
                    data-testid={`profile-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {PROFILE_FIELDS.filter((f) => !["city", "state", "country"].includes(f.key)).map((f) => {
                const val = profile[f.key] ?? "";
                const tel = String(val).replace(/[^\d+]/g, "");
                const flagged = uncertainFields.has(f.key);
                return (
                <div key={f.key} className={flagged ? "-m-1 rounded-lg bg-amber-400/[0.04] p-1 ring-1 ring-amber-400/50" : ""} data-testid={flagged ? `verify-field-${f.key}` : undefined}>
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <Label className="block text-xs uppercase tracking-wide text-zinc-400">{f.label}</Label>
                    {flagged && (
                      <span title="The scanner wasn't fully sure it read this correctly — double-check it against the sheet." className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-700 uppercase tracking-wide text-amber-200" data-testid={`verify-badge-${f.key}`}>
                        <AlertTriangle className="h-3 w-3" /> Verify
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={val}
                      onChange={(e) => setProfileField(f.key, e.target.value)}
                      className="flex-1 border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
                      data-testid={`profile-${f.key}`}
                    />
                    {f.key === "phone" && val && !values.gifted_to && (
                      <a href={`sms:${tel}`} title="Text guest" data-testid="profile-phone-text" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#9B2226]/50 bg-[#9B2226]/10 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20">
                        <MessageSquare className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>

        {!inline && (
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="preview-cancel-btn">
            <X className="mr-1.5 h-4 w-4" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={!!mixError} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E] disabled:opacity-50" data-testid="preview-save-btn">
            <Save className="mr-1.5 h-4 w-4" /> Save to Database
          </Button>
        </DialogFooter>
        )}

        <ImageZoomViewer
          open={viewer.open}
          src={viewer.src}
          alt={viewer.alt}
          onClose={() => setViewer({ open: false, src: null, alt: "" })}
          testid="tour-image-viewer"
        />

        <Dialog open={returnPromptOpen} onOpenChange={setReturnPromptOpen}>
          <DialogContent className="max-w-md border-zinc-800 bg-zinc-950" data-testid="return-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-display tracking-tight text-zinc-50">
                <User className="h-5 w-5 text-[#E0575B]" /> Is this a returning guest?
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-300">
                The sheet says <b className="text-zinc-100">{values.guest_name || "this guest"}</b>, which looks like{" "}
                <b className="text-zinc-100">{returnState?.name}</b> you've hosted before. If it's the same person,
                I'll mark this as a return and use your existing name so their history stays together.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <button onClick={declineReturnGuest} className="inline-flex items-center justify-center rounded-md border border-zinc-700 bg-transparent px-4 py-2 text-sm font-500 text-zinc-300 hover:bg-zinc-900" data-testid="return-decline-btn">
                No, new guest
              </button>
              <button onClick={confirmReturnGuest} className="inline-flex items-center justify-center rounded-md bg-[#9B2226] px-4 py-2 text-sm font-600 text-white hover:bg-[#7A1A1E]" data-testid="return-confirm-btn">
                Yes, same guest
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={sheetPromptOpen} onOpenChange={setSheetPromptOpen}>
          <DialogContent className="max-w-md border-zinc-800 bg-zinc-950" data-testid="sheet-reconcile-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-display tracking-tight text-zinc-50">
                <Sparkles className="h-5 w-5 text-[#E0575B]" /> {(pendingSheet?.conflicts?.length || 0)} field{(pendingSheet?.conflicts?.length || 0) === 1 ? "" : "s"} changed
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-300">
                The re-uploaded sheet reads differently from what's saved. Tap any field to keep yours instead — then update the ones you picked, or keep them all.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[46vh] space-y-2 overflow-y-auto" data-testid="sheet-reconcile-list">
              {(pendingSheet?.conflicts || []).map((c) => {
                const selKey = `${c.scope}-${c.key}`;
                const on = !!sheetSelected[selKey];
                return (
                  <button
                    type="button"
                    key={selKey}
                    onClick={() => toggleSheetField(c)}
                    aria-pressed={on}
                    className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors duration-150 ${on ? "border-[#9B2226]/60 bg-[#9B2226]/10" : "border-zinc-800 bg-zinc-900/60"}`}
                    data-testid={`sheet-change-${c.key}`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${on ? "border-[#E0575B] bg-[#9B2226] text-white" : "border-zinc-600 text-transparent"}`}
                      data-testid={`sheet-change-toggle-${c.key}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-600 text-zinc-400">{c.label}</span>
                      <span className="mt-0.5 flex items-center gap-2 text-sm">
                        <span className={on ? "text-zinc-500 line-through" : "font-600 text-zinc-100"}>{showSheetVal(c.oldVal)}</span>
                        <span className="text-zinc-500">→</span>
                        <span className={on ? "font-600 text-emerald-400" : "text-zinc-500 line-through"}>{showSheetVal(c.newVal)}</span>
                      </span>
                      <span className="mt-0.5 block text-[11px] text-zinc-500">{on ? "Using the sheet's value" : "Keeping yours"}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <button onClick={keepMineOnSheet} className="inline-flex items-center justify-center rounded-md border border-zinc-700 bg-transparent px-4 py-2 text-sm font-500 text-zinc-300 hover:bg-zinc-900" data-testid="sheet-keep-mine-btn">
                Keep mine
              </button>
              {(() => {
                const conf = pendingSheet?.conflicts || [];
                const sel = conf.filter((c) => sheetSelected[`${c.scope}-${c.key}`]).length;
                const label = sel === conf.length ? "Update all" : `Update selected (${sel})`;
                return (
                  <button
                    onClick={() => applySheetChanges(true)}
                    disabled={sel === 0}
                    className="inline-flex items-center justify-center rounded-md bg-[#9B2226] px-4 py-2 text-sm font-600 text-white transition-colors hover:bg-[#7A1A1E] disabled:cursor-not-allowed disabled:opacity-40"
                    data-testid="sheet-update-all-btn"
                  >
                    {label}
                  </button>
                );
              })()}
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </>
  );

  if (inline) {
    return (
      <div className="space-y-4" data-testid="tour-sheet-form">
        {body}
        {(dirty || newTour) && (
          <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full border border-zinc-700 bg-zinc-900/95 px-4 py-2 shadow-2xl backdrop-blur" style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))" }} data-testid="tour-save-bar">
            <span className="text-xs text-amber-300">{mixError ? "Mix must add up" : (dirty ? "Unsaved changes" : "Ready to save")}</span>
            {dirty && (
              <button onClick={handleDiscard} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100" data-testid="tour-discard-btn">
                <X className="h-4 w-4" /> Discard
              </button>
            )}
            <Button size="sm" onClick={handleSave} disabled={!!mixError} className="rounded-full bg-[#9B2226] text-white hover:bg-[#7A1A1E] disabled:opacity-50" data-testid="tour-save-check">
              <Check className="mr-1 h-4 w-4" /> {saveLabel || "Save"}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto lg-glass-strong border-0 rounded-3xl" data-testid="preview-dialog">
        {body}
      </DialogContent>
    </Dialog>
  );
};
